import {
    createAsyncThunk,
    createSlice,
    isAnyOf,
    PayloadAction,
} from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import IBaseState from '../../types/IBaseState';
import { AppState } from '../../reducers/root.reducer';
import { RequestServiceResponse } from '@tcc-online/request-service';
import ProjectTranslation from '../types/project/ProjectTranslation';
import Scale from '../types/project/360/Scale';
import UploadState from '../../types/UploadState';
import Admin360ScaleService from '../services/Admin360ScaleService';

const NAME = 'Admin360Scales';

const getTranslations = createAsyncThunk(
    `${NAME}/getTranslations`,
    Admin360ScaleService.getTranslations,
);
const getScales = createAsyncThunk(
    `${NAME}/getScales`,
    Admin360ScaleService.getScales,
);
const getScale = createAsyncThunk(
    `${NAME}/getScale`,
    Admin360ScaleService.getScale,
);
const createScale = createAsyncThunk(
    `${NAME}/createScale`,
    Admin360ScaleService.createScale,
);
const updateScale = createAsyncThunk(
    `${NAME}/updateScale`,
    Admin360ScaleService.updateScale,
);
const patchValues = createAsyncThunk(
    `${NAME}/patchValues`,
    Admin360ScaleService.patchValues,
);

export interface Admin360ScaleState {
    translations: IBaseState<ProjectTranslation[]>;
    scales: IBaseState<Scale[]>;
    detail: UploadState<Scale | undefined>;
}

const initialState: Admin360ScaleState = {
    translations: {
        data: [],
        loading: false,
    },
    scales: {
        data: [],
        loading: false,
    },
    detail: {
        data: undefined,
        loading: false,
        uploading: false,
    },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder): void => {
        builder.addCase(
            getScales.pending,
            (state: Admin360ScaleState): void => {
                state.scales.loading = true;
            },
        );
        builder.addCase(
            getScales.fulfilled,
            (
                state: Admin360ScaleState,
                action: PayloadAction<RequestServiceResponse<Scale[]>>,
            ): void => {
                state.scales.data = action.payload.data;
            },
        );

        builder.addCase(getScale.pending, (state: Admin360ScaleState): void => {
            state.detail.loading = true;
        });
        builder.addCase(
            getScale.fulfilled,
            (
                state: Admin360ScaleState,
                action: PayloadAction<RequestServiceResponse<Scale>>,
            ): void => {
                state.detail.data = action.payload.data;
            },
        );
        builder.addCase(
            createScale.pending,
            (state: Admin360ScaleState): void => {
                state.scales.loading = true;
            },
        );
        builder.addCase(
            createScale.fulfilled,
            (
                state: Admin360ScaleState,
                action: PayloadAction<RequestServiceResponse<Scale>>,
            ): void => {
                state.scales.data.push(action.payload.data);
            },
        );

        builder.addCase(
            updateScale.fulfilled,
            (
                state: Admin360ScaleState,
                action: PayloadAction<RequestServiceResponse<Scale>>,
            ): void => {
                state.detail.data = action.payload.data;
            },
        );

        builder.addCase(
            patchValues.fulfilled,
            (
                state: Admin360ScaleState,
                action: PayloadAction<RequestServiceResponse<Scale>>,
            ): void => {
                if (state.detail.data) {
                    state.detail.data = action.payload.data;
                }
            },
        );
        // builder.addCase(
        //     getTranslations.pending,
        //     (state: Admin360State): void => {
        //         state.translations.loading = true;
        //     },
        // );
        // builder.addCase(
        //     getTranslations.fulfilled,
        //     (
        //         state: Admin360State,
        //         action: PayloadAction<
        //             RequestServiceResponse<ProjectTranslation[]>
        //         >,
        //     ): void => {
        //         state.translations.data = action.payload.data;
        //     },
        // );
        //
        // builder.addMatcher(
        //     isAnyOf(getTranslations.fulfilled, getTranslations.rejected),
        //     (state: Admin360State): void => {
        //         state.translations.loading = false;
        //     },
        // );

        builder.addMatcher(
            isAnyOf(
                getScales.fulfilled,
                getScales.rejected,
                createScale.fulfilled,
                createScale.rejected,
            ),
            (state: Admin360ScaleState): void => {
                state.scales.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getScale.fulfilled, getScale.rejected),
            (state: Admin360ScaleState): void => {
                state.detail.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(updateScale.pending, patchValues.pending),
            (state: Admin360ScaleState): void => {
                state.detail.uploading = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                updateScale.fulfilled,
                updateScale.rejected,
                patchValues.fulfilled,
                patchValues.rejected,
            ),
            (state: Admin360ScaleState): void => {
                state.detail.uploading = false;
            },
        );
    },
});

const state = (state: AppState): Admin360ScaleState =>
    state.admin['360'].scales;

const Admin360ScaleSlice = {
    reducer: slice.reducer,
    actions: {
        getScales,
        getScale,
        createScale,
        updateScale,
        getTranslations,
        patchValues,
    },
    state: {
        all: createSelector(state, (state: Admin360ScaleState) => state),
        scales: createSelector(
            state,
            (state: Admin360ScaleState) => state.scales,
        ),
        detail: createSelector(
            state,
            (state: Admin360ScaleState) => state.detail,
        ),
        translations: createSelector(
            state,
            (state: Admin360ScaleState) => state.translations,
        ),
        translationsByLocale: createSelector(
            [state, (state: AppState, locale: string) => locale],
            (state: Admin360ScaleState, locale: string) =>
                state.translations?.data?.[locale],
        ),
    },
};

export default Admin360ScaleSlice;
